import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetNotificationMessagesQuery } from '../../../redux/api/notificationsApi'
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror'
import { liquid } from '@codemirror/lang-liquid'
import { Completion } from '@codemirror/autocomplete'
import { EditorView } from '@codemirror/view'
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { tags as highlightTags } from '@lezer/highlight'
import './TemplateHtmlEditor.scss'
import { Modal } from '../../common/Modals/Modal'

interface Props {
  visible: boolean
  templateName: string
  subject: string
  body: string
  onClose: () => void
  onConfirm: (values: { subject: string; body: string }) => void
}

const liquidHighlightTheme = HighlightStyle.define([
  { tag: highlightTags.brace, color: '#AF00DB', fontWeight: '600' },
  { tag: highlightTags.variableName, color: '#0070C1' },
  { tag: highlightTags.propertyName, color: '#001080' },
  { tag: highlightTags.function(highlightTags.variableName), color: '#795E26' },
  { tag: highlightTags.tagName, color: '#267F99', fontWeight: '600' },
  { tag: highlightTags.keyword, color: '#AF00DB' },
  { tag: highlightTags.controlKeyword, color: '#AF00DB', fontWeight: '600' },
  { tag: highlightTags.definitionKeyword, color: '#AF00DB', fontStyle: 'italic' },
  { tag: highlightTags.moduleKeyword, color: '#AF00DB' },
  { tag: highlightTags.operatorKeyword, color: '#AF00DB' },
  { tag: highlightTags.logicOperator, color: '#AF00DB' },
  { tag: highlightTags.atom, color: '#0000FF' },
  { tag: highlightTags.bool, color: '#0000FF' },
  { tag: highlightTags.number, color: '#098658' },
  { tag: highlightTags.string, color: '#A31515' },
  { tag: highlightTags.lineComment, color: '#008000', fontStyle: 'italic' },
  { tag: highlightTags.blockComment, color: '#008000', fontStyle: 'italic' },
  { tag: highlightTags.bracket, color: '#333333' },
  { tag: highlightTags.paren, color: '#333333' },
  { tag: highlightTags.derefOperator, color: '#666666' },
  { tag: highlightTags.punctuation, color: '#666666' },
  { tag: highlightTags.compareOperator, color: '#000080' },
  { tag: highlightTags.definitionOperator, color: '#000080' },
])

const editorChrome = EditorView.theme({
  '&': { fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px' },
  '&.cm-focused': { outline: 'none', borderColor: '#af00dbd3' },
  '.cm-gutters': { backgroundColor: 'transparent', borderRight: '1px solid #eee' },
  '.cm-activeLineGutter': { backgroundColor: 'transparent' },
})

export default function TemplateHtmlEditor({ visible, templateName, subject, body, onClose, onConfirm }: Props) {
  const { t } = useTranslation('common')
  const [subjectHtml, setSubjectHtml] = useState(subject)
  const [bodyHtml, setBodyHtml] = useState(body)
  const { currentData: variablesData } = useGetNotificationMessagesQuery()
  const subjectRef = useRef<ReactCodeMirrorRef>(null)
  const bodyRef = useRef<ReactCodeMirrorRef>(null)
  const lastFocusedEditor = useRef<'subject' | 'body'>('body')

  const variables = useMemo(() => {
    const properties = variablesData?.find((v) => v.name === templateName)?.properties
    if (!properties) return
    return Object.keys(properties)
  }, [templateName, variablesData])

  const insertVariable = (val: string) => {
    const editor = lastFocusedEditor.current === 'subject' ? subjectRef.current : bodyRef.current
    const view = editor?.view
    if (!view) return
    const { from, to } = view.state.selection.main
    const insert = `{{${val}}}`
    view.dispatch({ changes: { from, to, insert }, selection: { anchor: from + insert.length } })
    view.focus()
  }

  const completions: Completion[] | undefined = useMemo(() => {
    if (!variables) return
    return variables.map((p) => {
      return { label: p }
    })
  }, [variables])
  const extensions = [
    liquid({ variables: completions }),
    EditorView.lineWrapping,
    syntaxHighlighting(liquidHighlightTheme),
    editorChrome,
  ]
  const confirmContainer = (
    <div className="btnArea htmlEditApproval">
      <button type="button" onClick={onClose}>
        <i className="icon-close" /> {t('CANCEL')}
      </button>
      <button type="button" onClick={() => onConfirm({ subject: subjectHtml, body: bodyHtml })}>
        <i className="icon-check" /> {t('CONFIRM')}
      </button>
    </div>
  )
  return (
    <Modal
      title={`${templateName} - HTML Code`}
      icon="icon-code"
      visible={visible}
      onClose={onClose}
      children={
        <>
          <div className="htmlEditor" onKeyDown={(e) => e.stopPropagation()}>
            <div className="subjectCol">
              <label>Subject</label>
              <CodeMirror
                value={subjectHtml}
                extensions={extensions}
                height="100px"
                onChange={setSubjectHtml}
                onFocus={() => (lastFocusedEditor.current = 'subject')}
                indentWithTab
                ref={subjectRef}
                tabIndex={1}
              />
            </div>
            <div className="bodyCol">
              <label>Body</label>
              <CodeMirror
                value={bodyHtml}
                extensions={extensions}
                height="300px"
                onChange={setBodyHtml}
                onFocus={() => (lastFocusedEditor.current = 'body')}
                indentWithTab
                tabIndex={2}
                ref={bodyRef}
              />
            </div>
            <div className="variables">
              <label>Variables</label>
              {variables?.map((v) => (
                <span
                  key={v}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    insertVariable(v)
                  }}
                >
                  {v}
                </span>
              ))}
            </div>
          </div>
        </>
      }
      footer={confirmContainer}
    ></Modal>
  )
}
